import React from 'react';
import {
    Checkbox,
    ComboBox,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    IComboBoxOption,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Stack,
    Text,
    useTheme,
} from '@fluentui/react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {INormalizedProduct} from '../../models/NormalizedProduct';
import {DestinationType, IActiveDestination} from '../../models/Destination';
import {RootState} from '../../store/rootReducer';
import {setOpenBundleId} from '../../store/settingsSlice';
import {runBulkAddToDestination, runBulkListOnStorefront} from '../../store/bulkOperationsSlice';
import {useGetRecentWorkspaceRefsQuery, useSearchWorkspaceRefsQuery} from '../../store/Api';
import {useGetProductBundleRefsQuery} from '../../store/endpoints/ProductBundleEndpoints';
import {
    useGetStorefrontProductCountQuery,
    useGetStorefrontsQuery,
} from '../../store/endpoints/StorefrontSettingsEndpoints';
import {useGetOrganizationEntitlementsQuery} from '../../store/endpoints/OrganizationsEndpoints';
import {IBulkStorefrontListingRequest} from '../../models/BulkStorefront';
import NumberField from '../Inputs/NumberField';
import MarkupTypeSelection from '../Storefront/MarkupTypeSelection';
import useCan, {STOREFRONT_ADMIN} from '../../permissions/ability';
import {IToastAction} from './ToastAction';
import {storefrontProductsPath, STOREFRONT_PRODUCTS_TABS} from '../../logic/StorefrontRoutes';

interface IAddToDestinationDialogProps {
    isOpen: boolean;
    onDismiss: () => void;
    products: INormalizedProduct[];
    catalog: string;
    /** Called once the add has been dispatched, e.g. to clear the caller's row selection. */
    onAdded?: () => void;
}

interface IOption {
    id: string;
    name: string;
}

/**
 * Searchable single-select. Fluent's ComboBox type-ahead only jumps to a match; filtering the
 * option list as the user types is what makes a long bundle/storefront list usable.
 */
const SearchableSelect: React.FC<{
    options: IOption[];
    selectedId?: string;
    placeholder: string;
    disabled?: boolean;
    onSelect: (option: IOption) => void;
    /** Reported so a caller can run a server-side lookup for lists too large to hold client-side. */
    onFilterChange?: (text: string) => void;
}> = ({options, selectedId, placeholder, disabled, onSelect, onFilterChange}) => {
    const [filter, setFilter] = React.useState('');

    const updateFilter = (text: string) => {
        setFilter(text);
        onFilterChange?.(text);
    };

    const needle = filter.trim().toLowerCase();
    const visible = needle === ''
        ? options
        // Keep the current selection in the list; ComboBox reads its display text from `options`,
        // so filtering it out would blank the field.
        : options.filter(o => o.name.toLowerCase().includes(needle) || o.id === selectedId);

    const comboOptions: IComboBoxOption[] = visible.map(o => ({key: o.id, text: o.name}));

    return (
        <ComboBox
            placeholder={placeholder}
            options={comboOptions}
            selectedKey={selectedId ?? null}
            disabled={disabled}
            allowFreeform
            autoComplete="off"
            useComboBoxAsMenuWidth
            styles={{root: {width: '100%'}}}
            onInputValueChange={updateFilter}
            onMenuDismissed={() => updateFilter('')}
            onChange={(_e, option) => {
                if (!option) return;
                onSelect({id: option.key as string, name: option.text});
                updateFilter('');
            }}
        />
    );
};

/**
 * "Add To..." for the products selected in the search results.
 *
 * One place to send a selection to a workspace, a bundle, or a storefront. Before this, workspaces
 * and bundles could only be added one product at a time from the row menu, and storefront listing
 * was buried in the results overflow menu — three different flows for the same intent.
 *
 * Only opened from the command bar once products are selected, so the search screen isn't carrying
 * permanent add-to chrome.
 */
const AddToDestinationDialog: React.FC<IAddToDestinationDialogProps> = (props) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const history = useHistory();

    const org = useSelector((state: RootState) => state.settings.selectedOrganization);
    const listedInStorefront = useSelector((state: RootState) => state.search.criteria.inStorefront);
    const storefrontFilter = useSelector((state: RootState) => state.search.criteria.storefrontFilter);
    const desiredQuantity = useSelector((state: RootState) => state.search.desiredQuantity);

    const canStorefrontAdmin = useCan(STOREFRONT_ADMIN);

    // The recents endpoint only returns the five most recent workspaces, so typing in the picker
    // falls back to a server-side name search across all of them.
    const [workspaceFilter, setWorkspaceFilter] = React.useState('');
    const [debouncedWorkspaceFilter, setDebouncedWorkspaceFilter] = React.useState('');
    React.useEffect(() => {
        const handle = setTimeout(() => setDebouncedWorkspaceFilter(workspaceFilter), 400);
        return () => clearTimeout(handle);
    }, [workspaceFilter]);

    const workspaceSearchTerm = debouncedWorkspaceFilter.trim();
    const searchingWorkspaces = workspaceSearchTerm.length >= 2;

    const workspaces = useGetRecentWorkspaceRefsQuery();
    const workspaceMatches = useSearchWorkspaceRefsQuery(
        {searchText: workspaceSearchTerm},
        {skip: !searchingWorkspaces}
    );
    const bundles = useGetProductBundleRefsQuery({
        listedInStorefront: listedInStorefront,
        storefronts: storefrontFilter,
    });
    const storefronts = useGetStorefrontsQuery(undefined, {skip: !canStorefrontAdmin});
    const productCount = useGetStorefrontProductCountQuery(undefined, {skip: !canStorefrontAdmin});
    const entitlements = useGetOrganizationEntitlementsQuery(org ?? '', {skip: !org || org === ''});

    const optionsByType: {[key in DestinationType]: IOption[]} = {
        workspace: (searchingWorkspaces ? (workspaceMatches.data ?? []) : (workspaces.data ?? []))
            .filter(w => w.active)
            .map(w => ({id: w.id, name: w.name ?? 'Untitled workspace'})),
        bundle: (bundles.data?.items ?? [])
            .map(b => ({id: b.id ?? '', name: b.name ?? 'Untitled bundle'}))
            .filter(b => b.id !== ''),
        storefront: (storefronts.data?.items ?? [])
            .map(s => ({id: s.id ?? '', name: s.name ?? 'Untitled storefront'}))
            .filter(s => s.id !== ''),
    };

    // The dialog unmounts on close, so every open starts clean. Carrying the previous destination
    // over reads as stale state, and re-confirming a fresh pick is cheaper than noticing a wrong one.
    const [type, setType] = React.useState<DestinationType>('workspace');
    // Kept per type so flipping between the type buttons within one open doesn't lose a pick.
    const [selectedByType, setSelectedByType] = React.useState<{[key in DestinationType]?: IOption}>({});

    // Storefront listing settings, revealed once a storefront is picked.
    const [markup, setMarkup] = React.useState<number | undefined>();
    const [markupType, setMarkupType] = React.useState<string | undefined>();
    const [minimumPrice, setMinimumPrice] = React.useState<number | undefined>();
    const [featured, setFeatured] = React.useState<boolean>(false);
    const [overrideExisting, setOverrideExisting] = React.useState<boolean>(false);

    const selected = selectedByType[type];
    const productIds = props.products.map(p => p.id).filter(Boolean);
    const count = props.products.length;

    // Client-side entitlement warning (UX only — the server enforces the real limit).
    const currentCount = productCount.data?.count;
    const limit = entitlements.data?.storefrontProductLimit;
    const wouldExceedLimit = type === 'storefront'
        && currentCount != null && limit != null && currentCount + count > limit;

    const add = () => {
        if (!selected) return;

        const destination: IActiveDestination = {type, id: selected.id, name: selected.name};

        // Follow-up link on the completion toast. Bundles have no route, so instead of navigating
        // they ask the app-level BundleModalHost to open the editor in place.
        const action: IToastAction =
            type === 'workspace'
                ? {label: 'Open workspace', onClick: () => history.push(`/Workspace/${selected.id}`)}
                : type === 'storefront'
                    ? {
                        label: 'View storefront products',
                        onClick: () => history.push(storefrontProductsPath(selected.id, STOREFRONT_PRODUCTS_TABS.products)),
                    }
                    : {label: 'Open bundle', onClick: () => dispatch(setOpenBundleId(selected.id))};

        if (type === 'storefront') {
            const request: IBulkStorefrontListingRequest = {
                productIds,
                includedStorefronts: [selected.id],
                storefrontEntries: (markup != null || minimumPrice != null || (markupType && markupType !== ''))
                    ? {
                        [selected.id]: {
                            overrideMarkup: markup ?? undefined,
                            overrideMarkupType: markupType && markupType !== '' ? markupType : undefined,
                            minimumPrice: minimumPrice ?? undefined,
                        },
                    }
                    : undefined,
                featuredStorefronts: featured ? [selected.id] : [],
                // Tri-state: this dialog never opts a product into every storefront, so leave it alone.
                includeInAllStorefronts: null,
                storefrontOverrideMarkup: null,
                storefrontOverrideMarkupType: null,
                overrideExisting,
            };
            const label = `Listing ${count} product${count === 1 ? '' : 's'} on ${selected.name}…`;
            dispatch(runBulkListOnStorefront(props.catalog, request, label, action) as any);
        } else {
            // Quantity mirrors the row-level Add button, which has always applied the search
            // screen's desired quantity to workspace items.
            dispatch(runBulkAddToDestination(destination, props.products, {quantity: desiredQuantity}, action) as any);
        }

        props.onAdded?.();
        props.onDismiss();
    };

    const rows: {type: DestinationType; label: string; icon: string}[] = [
        {type: 'workspace', label: 'Workspace', icon: 'WorkItem'},
        {type: 'bundle', label: 'Bundle', icon: 'ProductList'},
        ...(canStorefrontAdmin ? [{type: 'storefront' as DestinationType, label: 'Storefront', icon: 'Shop'}] : []),
    ];

    const activeRow = rows.find(r => r.type === type) ?? rows[0];
    const activeSelection = selectedByType[activeRow.type];
    const sourceOptions = optionsByType[activeRow.type];
    // The workspace list swaps from recents to search results as the user types, which can drop the
    // option they already picked — and ComboBox reads its display text from `options`, so losing it
    // would blank the field. Keep the current selection present regardless of the source list.
    const activeOptions = activeSelection && !sourceOptions.some(o => o.id === activeSelection.id)
        ? [activeSelection, ...sourceOptions]
        : sourceOptions;

    return (
        <Dialog
            hidden={!props.isOpen}
            onDismiss={props.onDismiss}
            dialogContentProps={{
                type: DialogType.close,
                title: `Add ${count} product${count === 1 ? '' : 's'} to...`,
            }}
            modalProps={{isBlocking: false}}
            minWidth={640}
            maxWidth={720}
        >
            <Stack tokens={{childrenGap: '1em'}}>
                {wouldExceedLimit &&
                    <MessageBar messageBarType={MessageBarType.warning}>
                        This batch of {count} products would bring your storefront listings
                        ({currentCount}) past your limit of {limit}. The server may reject the batch.
                    </MessageBar>}

                {/* Segmented type picker; only the selected type's picker is shown below it. */}
                <Stack horizontal tokens={{childrenGap: 0}}>
                    {rows.map(row => (
                        <DefaultButton
                            key={row.type}
                            text={row.label}
                            iconProps={{iconName: row.icon}}
                            toggle
                            checked={type === row.type}
                            primary={type === row.type}
                            onClick={() => setType(row.type)}
                        />
                    ))}
                </Stack>

                <SearchableSelect
                    // Remount per type: the filter text lives inside the component, and carrying it
                    // across a type switch would silently filter the new list by the old search.
                    key={activeRow.type}
                    options={activeOptions}
                    selectedId={activeSelection?.id}
                    // Never disabled for workspaces: an empty recents list still has to be typeable,
                    // since that's what triggers the search past the recents.
                    disabled={activeRow.type !== 'workspace' && activeOptions.length === 0}
                    placeholder={activeOptions.length === 0 && activeRow.type !== 'workspace'
                        ? `No ${activeRow.label.toLowerCase()}s available`
                        : `Search ${activeRow.label.toLowerCase()}s...`}
                    onFilterChange={activeRow.type === 'workspace' ? setWorkspaceFilter : undefined}
                    onSelect={(option) => setSelectedByType(prev => ({...prev, [activeRow.type]: option}))}
                />
                {activeRow.type === 'workspace' &&
                    <Text variant="small" styles={{root: {color: theme.palette.neutralSecondary, marginTop: '-0.5em'}}}>
                        {workspaceMatches.isFetching
                            ? 'Searching workspaces…'
                            : searchingWorkspaces
                                ? `${activeOptions.length} match${activeOptions.length === 1 ? '' : 'es'}`
                                : 'Showing recent workspaces — type to search all of them.'}
                    </Text>}

                {type === 'storefront' && selectedByType.storefront &&
                    <Stack tokens={{childrenGap: '0.5em'}}>
                        <Stack horizontal wrap verticalAlign="end" tokens={{childrenGap: '0.5em'}}>
                            <div style={{width: 120}}>
                                <NumberField label="Markup" value={markup} decimal onChange={setMarkup}/>
                            </div>
                            <div style={{width: 160}}>
                                <MarkupTypeSelection label="Markup Type" value={markupType} onChange={setMarkupType}/>
                            </div>
                            <div style={{width: 140}}>
                                <NumberField label="Minimum Price" prefix="$" value={minimumPrice} decimal onChange={setMinimumPrice}/>
                            </div>
                        </Stack>
                        <Checkbox label="Featured" checked={featured} onChange={(_e, v) => setFeatured(v ?? false)}/>
                        <Checkbox
                            label="Override existing storefront settings"
                            checked={overrideExisting}
                            onChange={(_e, v) => setOverrideExisting(v ?? false)}
                        />
                        <Text variant="small" styles={{root: {color: theme.palette.neutralSecondary}}}>
                            When off, products already on this storefront keep their current markup.
                        </Text>
                    </Stack>}
            </Stack>

            <DialogFooter>
                <PrimaryButton
                    text="Add"
                    iconProps={{iconName: 'Add'}}
                    disabled={!selected}
                    onClick={add}
                />
                <DefaultButton text="Cancel" onClick={props.onDismiss}/>
            </DialogFooter>
        </Dialog>
    );
};

export default AddToDestinationDialog;
